/**
 * Two-way conversion between the plain-text-with-markers storage format
 * (**bold**, _italic_, "- " bullet lines — same convention `renderRichText`
 * already knows how to display) and the DOM a contenteditable region uses.
 *
 * Why this exists: a plain <textarea> has no formatting engine, so Cmd/Ctrl+B
 * does nothing in one — that's not a bug, it's just what a textarea is.
 * contenteditable regions DO respond to real OS/browser keyboard shortcuts
 * natively (Cmd/Ctrl+B, Cmd/Ctrl+I, and the native selection toolbar on
 * mobile) with no JS required for the shortcut itself. This module only
 * handles translating that DOM back to the plain-marker format so storage,
 * rendering (renderRichText), and every existing reader of this content
 * (course transcripts, sequence content, announcements) stays unchanged.
 *
 * Deliberately never assigns raw/untrusted content via innerHTML — markersToHtml
 * escapes source text before building tags, exactly like renderRichText
 * builds React elements instead of using dangerouslySetInnerHTML.
 */

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/** Converts "**bold**", "_italic_" and "^^soft grey^^" markers in already-escaped text into real tags, and newlines into <br>. */
const inlineMarkersToHtml = (escapedText) => escapedText
  .replace(/\^\^([^^]+)\^\^/g, '<span data-grey="1" style="color:#6B7280">$1</span>')
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/_([^_]+)_/g, '<em>$1</em>')
  .replace(/\n/g, '<br>');

/**
 * Plain marker text -> HTML string for setting a contenteditable's innerHTML.
 * Blocks (blank-line separated, same split as renderRichText) become one
 * <div> each, except a block where every line is a bullet, which becomes
 * a real <ul>.
 */
const isBulletBlock = (block) => {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  return lines.length > 0 && lines.every(l => /^[-•*]\s+/.test(l));
};

export const markersToHtml = (text) => {
  if (!text) return '';
  const rawBlocks = text.replace(/\u00A0/g, ' ').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);

  // See server/src/utils/richTextEmail.js's matching fix — merges a run of
  // separately-blank-line-split single-bullet blocks (how paste can produce
  // them) into one real shared list instead of each showing a lone bullet.
  const blocks = [];
  for (const block of rawBlocks) {
    if (isBulletBlock(block) && blocks.length > 0 && isBulletBlock(blocks[blocks.length - 1])) {
      blocks[blocks.length - 1] += '\n' + block;
    } else {
      blocks.push(block);
    }
  }

  return blocks.map(block => {
    const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
    const isBulletList = isBulletBlock(block);
    if (isBulletList) {
      // Explicit inline styles — see server/src/utils/richTextEmail.js's
      // matching fix. Kept here too so anything rendering this HTML
      // directly (not just the contenteditable's own Tailwind classes)
      // gets the same real hanging indent.
      const items = lines.map(l => `<li style="margin-bottom:6px">${inlineMarkersToHtml(escapeHtml(l.replace(/^[-•*]\s+/, '')))}</li>`).join('');
      return `<ul style="margin:0 0 12px;padding-left:20px;list-style-position:outside">${items}</ul>`;
    }
    return `<div>${inlineMarkersToHtml(escapeHtml(block))}</div>`;
  }).join('');
};

/**
 * Recursively serializes a DOM node's inline formatting to marker text.
 * Checks both semantic tags (<b>/<strong>, <i>/<em> — what our own editor
 * produces) and inline CSS (font-weight/font-style — how Google Sheets and
 * Excel commonly encode formatting in pasted clipboard HTML), so a range
 * copied from a real spreadsheet keeps its real Bold/Italic either way.
 */
export const serializeInline = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  if (node.tagName === 'BR') return '\n';

  const inner = Array.from(node.childNodes).map(serializeInline).join('');
  if (!inner.trim()) return inner;

  const style = node.getAttribute ? (node.getAttribute('style') || '') : '';
  const isBold = node.tagName === 'B' || node.tagName === 'STRONG' || /font-weight\s*:\s*(bold|[6-9]\d\d)/i.test(style);
  const isItalic = node.tagName === 'I' || node.tagName === 'EM' || /font-style\s*:\s*italic/i.test(style);

  // Soft grey (Jenn, Nov 3: a gentle note after Patti's line) — only our own toolbar's span, never pasted colours.
  const isGrey = !!(node.getAttribute && node.getAttribute('data-grey'));

  let result = inner;
  if (isItalic) result = `_${result}_`;
  if (isBold) result = `**${result}**`;
  if (isGrey) result = `^^${result}^^`;
  return result;
};

/**
 * contenteditable root element -> plain marker text. Each top-level DIV/P
 * (one hard Enter in the editor) becomes its own paragraph block; a UL/OL
 * becomes one bullet block. Blocks join with a blank line, matching
 * renderRichText's paragraph-splitting.
 */
export const htmlToMarkers = (root) => {
  const blocks = [];
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'UL' || node.tagName === 'OL')) {
      const items = Array.from(node.children)
        .filter(li => li.tagName === 'LI')
        .map(li => `- ${serializeInline(li).trim()}`)
        .filter(l => l !== '-');
      if (items.length) blocks.push(items.join('\n'));
      continue;
    }
    const text = serializeInline(node).trim();
    if (text) blocks.push(text);
  }
  // Root with no block-level children yet (e.g. freshly focused, still one text node) — treat as a single block.
  if (blocks.length === 0) {
    const text = serializeInline(root).trim();
    return text;
  }
  return blocks.join('\n\n');
};
